import React, { useMemo, useState, useEffect } from 'react'
import { useProductAnalysisDeepDive } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import GlobalHeader from '../components/GlobalHeader'
import Badge from '../components/Badge'
import InfoTooltip from '../components/InfoTooltip'
import { 
  Package, 
  TrendingUp, 
  TrendingDown, 
  Search,
  DollarSign,
  ShoppingCart,
  Calculator,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react'

export default function ProductAnalysisImproved() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('revenue')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  // Fetch data
  const { data: analysisData, isLoading, error, refetch } = useProductAnalysisDeepDive(
    selectedFiscalWeek,
    searchTerm,
    sortField,
    sortDirection,
    currentPage,
    50
  )

  useEffect(() => {
    if (analysisData) {
      console.log('analysisData', analysisData)
    }
  }, [analysisData])

  const productsData = analysisData?.products ?? []
  const sortedProducts = useMemo(() => {
    const sortable = [...productsData]
    const compare = (a: any, b: any) => {
      const dirMultiplier = sortDirection === 'asc' ? 1 : -1

      switch (sortField) {
        case 'units':
          return ((a.units ?? 0) - (b.units ?? 0)) * dirMultiplier
        case 'asp':
          return ((a.asp ?? 0) - (b.asp ?? 0)) * dirMultiplier
        case 'gm_percent':
          return ((a.gm_percent ?? 0) - (b.gm_percent ?? 0)) * dirMultiplier
        case 'revenue':
        default:
          return ((a.revenue ?? 0) - (b.revenue ?? 0)) * dirMultiplier
      }
    }

    sortable.sort(compare)
    return sortable
  }, [productsData, sortField, sortDirection])

  const formatCurrency = (value: number | null | undefined, compact = false) => {
    if (value === null || value === undefined) return '—'
    if (compact && Math.abs(value) >= 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—'
    return `${(value * 100).toFixed(1)}%`
  }

  const formatRatio = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—'
    return value.toFixed(2)
  }

  const formatWOS = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—'
    return `${value.toFixed(1)}w`
  }

  const getBadgeVariant = (badge: string) => {
    switch (badge.toLowerCase()) {
      case 'high margin':
      case 'excellent':
      case 'in stock':
        return 'success'
      case 'warning':
      case 'moderate':
      case 'out of stock':
        return 'warning'
      case 'critical':
      case 'poor':
        return 'danger'
      default:
        return 'neutral'
    }
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Product Analysis"
          subtitle="Loading comprehensive product performance analysis..."
          isLoading={true}
        />
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-muted rounded-lg"></div>
          <div className="h-96 bg-muted rounded-lg"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Product Analysis"
          subtitle="Error loading product data"
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
          <p className="text-destructive">Failed to load product analysis data. Please try again.</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const summary = analysisData?.summary

  return (
    <div className="space-y-6">
      {/* Global Header */}
      <GlobalHeader 
        title="Product Analysis"
        subtitle="Comprehensive pricing, profitability, and distribution analysis"
        onRefresh={() => refetch()}
        isLoading={isLoading}
      />

      {/* Summary Statistics - Compact Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <Package className="w-4 h-4 text-blue-500" />
            <div>
              <p className="text-xs text-muted-foreground">Total SKUs</p>
              <p className="text-lg font-semibold text-foreground">{summary?.totalSKUs || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            <div>
              <p className="text-xs text-muted-foreground">Weighted ASP</p>
              <p className="text-lg font-semibold text-foreground">{formatCurrency(summary?.weightedASP ?? null, true)}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <Calculator className="w-4 h-4 text-purple-500" />
            <div>
              <p className="text-xs text-muted-foreground">Median GM%</p>
              <p className="text-lg font-semibold text-foreground">{formatPercent((summary as any)?.medianGM ?? null)}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <BarChart3 className="w-4 h-4 text-orange-500" />
            <div>
              <p className="text-xs text-muted-foreground">Avg Discount</p>
              <p className="text-lg font-semibold text-foreground">{formatPercent(summary?.avgDiscount ?? null)}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div>
              <p className="text-xs text-muted-foreground">Est Lost Sales</p>
              <p className="text-lg font-semibold text-foreground">{formatCurrency(summary?.totalEstLostDollars ?? null, true)}</p>
            </div>
          </div>
        </div>

        <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center space-x-2">
            <Package className="w-4 h-4 text-indigo-500" />
            <div>
              <p className="text-xs text-muted-foreground">Median WOS</p>
              <p className="text-lg font-semibold text-foreground">{formatWOS((summary as any)?.medianWOS ?? null)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Search & Filters */}
      <div className="bg-background/80 border border-border/40 rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Search products or SKUs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border/40 rounded-lg bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Sort by:</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className="px-3 py-2 border border-border/40 rounded-lg bg-background/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="revenue">Revenue</option>
              <option value="units">Units</option>
              <option value="asp">ASP</option>
              <option value="gm_percent">GM%</option>
            </select>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="p-2 hover:bg-muted/30 rounded-lg transition-colors"
            >
              {sortDirection === 'asc' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Compact Product Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {sortedProducts.map((product, index) => (
          <div key={`${product.sku}-${index}`} className="bg-card border border-border/30 rounded-lg overflow-hidden hover:shadow-md transition-all duration-200">
            {/* Product Header with Light Background */}
            <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-b border-border/20 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-sm leading-tight mb-1 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-2">{product.sku}</p>
                  {/* Compact Badges */}
                  <div className="flex flex-wrap gap-1">
                    {(product.badges || []).map((badge) => (
                      <Badge key={badge} variant={getBadgeVariant(badge)} size="sm">
                        {badge}
                      </Badge>
                    ))}
                    {product.gm_percent && product.gm_percent > 0.4 && (
                      <Badge variant="success" size="sm">
                        High Margin
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right ml-2">
                  <div className="text-lg font-bold text-foreground">{formatCurrency(product.revenue, true)}</div>
                  <div className="text-xs text-muted-foreground">{product.units.toLocaleString()} units</div>
                </div>
              </div>
            </div>

            {/* Product Details Section */}
            <div className="p-4">

            {/* Compact Metrics Grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              {/* Pricing Metrics */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">ASP</span>
                  <span className="font-medium">{formatCurrency(product.asp)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Retail</span>
                  <span className="text-muted-foreground">{formatCurrency((product as any).retail_price)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Discount</span>
                  <span className={product.discount_pct && product.discount_pct > 0.2 ? "text-orange-500" : "text-muted-foreground"}>
                    {formatPercent(product.discount_pct)}
                  </span>
                </div>
              </div>

              {/* Profitability Metrics */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">GM%</span>
                  <span className={`font-medium ${product.gm_percent && product.gm_percent > 0.4 ? "text-green-600" : product.gm_percent && product.gm_percent > 0.2 ? "text-yellow-600" : "text-red-600"}`}>
                    {formatPercent(product.gm_percent)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">GM $</span>
                  <span className="font-medium">{formatCurrency(product.gm_dollars)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Turn Rate</span>
                  <span className="text-muted-foreground">{formatRatio((product as any).turn_rate)}</span>
                </div>
              </div>
            </div>

            {/* Inventory & Distribution - Compact */}
            <div className="mt-3 pt-3 border-t border-border/20">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Store WOS</span>
                    <span className={product.store_wos && product.store_wos > 8 ? "text-orange-500" : "text-muted-foreground"}>
                      {formatWOS(product.store_wos)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">DC WOS</span>
                    <span className={product.dc_wos && product.dc_wos > 12 ? "text-orange-500" : "text-muted-foreground"}>
                      {formatWOS(product.dc_wos)}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Distribution</span>
                    <span className="text-muted-foreground">{formatPercent(product.distribution_pct)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">OOS Rate</span>
                    <span className={(product as any).oos_pct && (product as any).oos_pct > 0.05 ? "text-red-500" : "text-green-600"}>
                      {formatPercent((product as any).oos_pct)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

              {/* Expandable Detailed View */}
              <div className="mt-3 pt-3 border-t border-border/20">
                <button
                  onClick={() => toggleSection(`details-${product.sku}`)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>Detailed View</span>
                  {expandedSections[`details-${product.sku}`] ? 
                    <ChevronDown className="w-3 h-3" /> : 
                    <ChevronRight className="w-3 h-3" />
                  }
                </button>
                
                {expandedSections[`details-${product.sku}`] && (
                  <div className="mt-3 space-y-3 pt-3 border-t border-border/20">
                    {/* Additional Detailed Metrics */}
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div className="space-y-2">
                        <div className="font-medium text-muted-foreground mb-2">Performance</div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>Performance Level</span>
                            <span className="font-medium">{(product as any).performance_level || "—"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Store Count</span>
                            <span className="text-muted-foreground">{product.store_count ? product.store_count.toLocaleString() : "—"}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="font-medium text-muted-foreground mb-2">Advanced</div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>Est Lost Sales</span>
                            <span className="text-orange-600">{formatCurrency((product as any).est_lost_sales)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>GMROII</span>
                            <span className="text-muted-foreground">{(product as any).gmroii ? ((product as any).gmroii).toFixed(2) + 'x' : "—"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Debug Info */}
      {analysisData?.meta && (
        <div className="bg-muted/20 border border-border/40 rounded-lg p-4">
          <h3 className="font-medium mb-2">Debug Info</h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Products loaded: {productsData.length}</p>
            <p>• Skipped rows: {analysisData.meta.dataQuality?.skippedRows || 'N/A'}</p>
            <p>• Total active stores: {analysisData.meta.dataQuality?.totalActiveStores || 'N/A'}</p>
            <p>• Week: {analysisData.meta.week || 'N/A'}</p>
          </div>
        </div>
      )}
    </div>
  )
}